import React, { useState, useEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import { useVoiceChatContext } from '../../contexts/VoiceChatContext';

const ChatInput = ({ onSendMessage }) => {
  const [message, setMessage] = useState('');
  const [isSending, setIsSending] = useState(false);
  const voiceChat = useVoiceChatContext();
  const wasRecording = useRef(false);
  const textareaRef = useRef(null);
  const hadTypedInput = useRef(false);

  // Show live transcript in input field while recording
  useEffect(() => {
    if (voiceChat.isRecording) {
      wasRecording.current = true;
      if (voiceChat.transcript) {
        setMessage(voiceChat.transcript);
      }
    } else if (wasRecording.current && !voiceChat.isRecording && !voiceChat.isProcessing) {
      // Just stopped recording - auto-send the transcript
      wasRecording.current = false;

      const transcriptToSend = voiceChat.transcript;
      
      if (transcriptToSend && transcriptToSend.trim()) {
        // Auto-send
        (async () => {
          setIsSending(true);
          try {
          await onSendMessage(transcriptToSend.trim(), { isVoice: true });
            setMessage('');
          } catch (err) {
            console.error('Error sending voice message:', err);
          } finally {
            setIsSending(false);
          }
        })();
      } else {
        setMessage('');
      }
    }
  }, [voiceChat.isRecording, voiceChat.transcript, voiceChat.isProcessing, message, onSendMessage]);

  // Refocus textarea after sending a typed message
  useEffect(() => {
    if (!isSending && hadTypedInput.current) {
      hadTypedInput.current = false;
      textareaRef.current?.focus();
    }
  }, [isSending]);

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!message.trim() || isSending) return;

    if (voiceChat.isSpeaking) voiceChat.stopSpeaking();

    hadTypedInput.current = true;
    setIsSending(true);
    try {
      await onSendMessage(message.trim(), { isVoice: false });
      setMessage('');
    } catch (err) {
      console.error('Error sending message:', err);
    } finally {
      setIsSending(false);
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSubmit(e);
    }
  };

  // Unified button click handler
  const handleButtonClick = async () => {
    if (voiceChat.isRecording) {
      // Stop recording - the useEffect will auto-send
      await voiceChat.stopRecording();
    } else if (message.trim()) {
      // Send text message
      await handleSubmit({ preventDefault: () => {} });
    } else if (voiceChat.isSpeaking) {
      voiceChat.stopSpeaking();
    } else if (voiceChat.isConnected) {
      // Start recording
      await voiceChat.startRecording();
    }
  };

  // Determine button state
  const getButtonState = () => {
    if (isSending || voiceChat.isProcessing) {
      return { icon: '⏳', disabled: true, title: 'Processing...' };
    }
    if (voiceChat.isRecording) {
      return { icon: '⏹', disabled: false, title: 'Click square to send response' };
    }
    if (message.trim()) {
      return { icon: '▶', disabled: false, title: 'Send message' };
    }
    if (voiceChat.isSpeaking) {
      return { icon: '⏹', disabled: false, title: 'Stop playback' };
    }
    if (!voiceChat.isConnected) {
      return { icon: '🎤', disabled: true, title: 'Connecting...' };
    }
    return { icon: '🎤', disabled: false, title: 'Start voice recording' };
  };

  const buttonState = getButtonState();

  return (
    <div className="chat-input-container">
      <form onSubmit={handleSubmit} className="chat-input-form">
        <div className="input-with-actions">
          <textarea
            ref={textareaRef}
            className="chat-input-textarea"
            value={message}
            onChange={(e) => {
              setMessage(e.target.value);
              if (voiceChat.isSpeaking) voiceChat.stopSpeaking();
            }}
            onKeyDown={handleKeyDown}
            placeholder={voiceChat.isRecording ? "Listening..." : "Type your message or use voice..."}
            rows={3}
            disabled={isSending || voiceChat.isRecording}
          />
          <button
            type="button"
            className={`unified-action-button ${buttonState.disabled ? 'disabled' : ''} ${voiceChat.isRecording ? 'recording' : ''}`}
            onClick={handleButtonClick}
            disabled={buttonState.disabled}
            title={buttonState.title}
          >
            {buttonState.icon}
          </button>
        </div>
      </form>
    </div>
  );
};

ChatInput.propTypes = {
  onSendMessage: PropTypes.func.isRequired,
};

export default ChatInput;